// src/components/InstitucionInscripcionForm.js
//
// Wizard de inscripción de una institución a la red — mismo patrón
// visual que CooperativaWizardModal.js de fenagro_front (Stepper de
// pasos dentro de un Dialog, header con degradado verde).
//
// 3 pasos (más simple que el de cooperativas — sin OCR de documentos,
// ya que la institución educativa no requiere ese flujo legal):
//   1. Datos generales
//   2. Ubicación (país/departamento/municipio reales de Ecolab)
//   3. Confirmación y envío
//
// Requiere sesión iniciada (el backend exige [Authorize] en POST /instituciones).

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, TextField, Button, IconButton,
  Stepper, Step, StepLabel, Alert, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Divider, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { institucionService, geografiaService } from '../services/api';
import UbicacionMapaPicker from './UbicacionMapaPicker';

const STEPS = ['Datos generales', 'Ubicación', 'Admin de la alianza', 'Confirmación'];

const TIPOS_DOCUMENTO = [
  { value: 'CC', label: 'Cédula de ciudadanía' },
  { value: 'CE', label: 'Cédula de extranjería' },
  { value: 'PA', label: 'Pasaporte' },
  { value: 'NIT', label: 'NIT' },
];

const TIPOS_INSTITUCION = [
  { value: 'Colegio', label: 'Colegio' },
  { value: 'Universidad', label: 'Universidad' },
  { value: 'InstitutoTecnico', label: 'Instituto Técnico' },
  { value: 'CentroFormacion', label: 'Centro de Formación' },
  { value: 'CooperativaEducativa', label: 'Cooperativa Educativa' },
  { value: 'CentroFormacionTrabajo', label: 'Centro de Formación para el Trabajo' },
  { value: 'Otro', label: 'Otro' },
];

const FORM_INICIAL = {
  nombre: '',
  nombreCorto: '',
  nit: '',
  tipo: 'Colegio',
  email: '',
  telefono: '',
  sitioWeb: '',
  paisId: 1045,
  paisNombre: 'Colombia',
  departamentoId: '',
  departamentoNombre: '',
  municipioId: '',
  municipioNombre: '',
  direccion: '',
  latitud: '',
  longitud: '',
  ecolabEntidadId: '',
  ecolabTipoEntidad: '',
  // ── Admin de la alianza (SuperAdmin institucional) ────────────────
  adminNombre: '',
  adminApellido: '',
  adminEmail: '',
  adminCelular: '',
  adminDocumento: '',
  adminTipoDocumento: 'CC',
};

export default function InstitucionInscripcionForm({ open, onClose, onSuccess }) {
  const nombreProducto = useTheme().productoInfo?.nombre || 'EducaT';
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(FORM_INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [creada, setCreada] = useState(null);

  const [deptos, setDeptos] = useState([]);
  const [munis, setMunis] = useState([]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setForm(FORM_INICIAL);
    setError(null);
    setCreada(null);
  }, [open]);

  useEffect(() => {
    console.log('[InstitucionForm] useEffect departamentos — open:', open, 'paisId:', form.paisId);
    if (!open) return;
    console.log('[InstitucionForm] Llamando geografiaService.getDepartamentos(' + form.paisId + ')...');
    geografiaService.getDepartamentos(form.paisId)
      .then((data) => {
        console.log('[InstitucionForm] Departamentos recibidos:', data);
        setDeptos(data);
      })
      .catch((err) => {
        console.error('[InstitucionForm] Error cargando departamentos:', err);
        setDeptos([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.paisId]);

  useEffect(() => {
    if (!form.departamentoId) { setMunis([]); return; }
    geografiaService.getMunicipiosConCoordenadas(form.departamentoId).then(setMunis).catch(() => setMunis([]));
  }, [form.departamentoId]);

  // Estado del botón "Usar mi ubicación"
  const [buscandoUbicacion, setBuscandoUbicacion] = useState(false);
  const [errorUbicacion, setErrorUbicacion] = useState(null);

  const usarMiUbicacion = useCallback(() => {
    if (!navigator.geolocation) {
      setErrorUbicacion('Tu navegador no soporta geolocalización.');
      return;
    }
    setBuscandoUbicacion(true);
    setErrorUbicacion(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const cercano = await geografiaService.getMunicipioMasCercano(latitude, longitude, form.paisId);
          if (!cercano) {
            setErrorUbicacion('No se pudo determinar tu municipio. Selecciónalo manualmente.');
            setBuscandoUbicacion(false);
            return;
          }
          // Cargar los municipios del departamento detectado antes de seleccionar
          const municipiosDelDepto = await geografiaService.getMunicipiosConCoordenadas(cercano.departamentoID);
          setMunis(municipiosDelDepto);
          setForm((f) => ({
            ...f,
            departamentoId: String(cercano.departamentoID),
            municipioId: String(cercano.municipioID),
            latitud: String(latitude),
            longitud: String(longitude),
          }));
        } catch {
          setErrorUbicacion('Error consultando tu ubicación. Selecciónala manualmente.');
        } finally {
          setBuscandoUbicacion(false);
        }
      },
      (err) => {
        setBuscandoUbicacion(false);
        if (err.code === err.PERMISSION_DENIED) {
          setErrorUbicacion('Permiso de ubicación denegado. Actívalo en tu navegador o selecciona manualmente.');
        } else {
          setErrorUbicacion('No se pudo obtener tu ubicación. Selecciónala manualmente.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [form.paisId]);

  const set = useCallback((campo, valor) => setForm((f) => ({ ...f, [campo]: valor })), []);

  const setLatLng = useCallback(({ lat, lng }) => {
    setForm((f) => ({ ...f, latitud: lat != null ? String(lat) : '', longitud: lng != null ? String(lng) : '' }));
  }, []);

  const puedeAvanzar = useMemo(() => {
    if (step === 0) return !!(form.nombre.trim() && form.email.trim());
    if (step === 1) return !!(form.departamentoId && form.municipioId);
    if (step === 2) return !!(form.adminNombre.trim() && form.adminEmail.trim());
    return true;
  }, [step, form]);

  const atras = () => setStep((s) => Math.max(s - 1, 0));
  const siguiente = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));

  const enviar = async () => {
    setError(null);
    setEnviando(true);
    try {
      const departamento = deptos.find((d) => String(d.departamentoID) === String(form.departamentoId));
      const municipio = munis.find((m) => String(m.municipioID) === String(form.municipioId));

      const result = await institucionService.create({
        name: form.nombre,
        shortName: form.nombreCorto || undefined,
        nit: form.nit || undefined,
        type: form.tipo,
        email: form.email,
        phone: form.telefono || undefined,
        website: form.sitioWeb || undefined,
        countryId: form.paisId,
        countryName: form.paisNombre,
        departmentId: parseInt(form.departamentoId, 10),
        departmentName: departamento?.nombre,
        municipalityId: parseInt(form.municipioId, 10),
        municipalityName: municipio?.nombre,
        address: form.direccion || undefined,
        latitude: form.latitud ? parseFloat(form.latitud) : undefined,
        longitude: form.longitud ? parseFloat(form.longitud) : undefined,
        ecolabEntidadId: form.ecolabEntidadId ? parseInt(form.ecolabEntidadId, 10) : undefined,
        ecolabTipoEntidad: form.ecolabTipoEntidad || undefined,
        // Admin de la alianza -- EducaT lo crea en Ecolab (rol "Aliado")
        // y le manda el correo de bienvenida con "¿Olvidé mi contraseña?".
        adminNombre: form.adminNombre,
        adminApellido: form.adminApellido || undefined,
        adminEmail: form.adminEmail,
        adminCelular: form.adminCelular || undefined,
        adminDocumento: form.adminDocumento || undefined,
        adminTipoDocumento: form.adminTipoDocumento || undefined,
      });
      setCreada(result);
      onSuccess?.(result);
    } catch (e) {
      setError(e?.message || 'No se pudo registrar la institución.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BusinessIcon />
          <Box>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Registrar institución / alianza
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Red educativa FENAGRO APL — {nombreProducto}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }} disabled={enviando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: '32px !important' }}>
        {creada ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%', bgcolor: '#e8f5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
            }}>
              <BusinessIcon sx={{ fontSize: 36, color: '#2e7d32' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1a4d2e' }}>
              ¡Institución registrada!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              <strong>{form.nombre}</strong> ya está en la red.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Se creó la cuenta de <strong>{form.adminNombre}</strong> como administrador(a) de la
              institución. Le llegará un correo a <strong>{form.adminEmail}</strong> para definir su
              contraseña e ingresar al portal.
            </Typography>
          </Box>
        ) : (
          <>
            <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
              {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Paso 1 — Datos generales */}
            {step === 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Datos generales</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField size="small" fullWidth required label="Nombre de la institución"
                      value={form.nombre} onChange={(e) => set('nombre', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Nombre corto / siglas"
                      value={form.nombreCorto} onChange={(e) => set('nombreCorto', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Tipo de institución</InputLabel>
                      <Select value={form.tipo} label="Tipo de institución" onChange={(e) => set('tipo', e.target.value)}>
                        {TIPOS_INSTITUCION.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="NIT" value={form.nit} onChange={(e) => set('nit', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth required label="Email institucional"
                      value={form.email} onChange={(e) => set('email', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Teléfono"
                      value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField size="small" fullWidth label="Sitio web"
                      value={form.sitioWeb} onChange={(e) => set('sitioWeb', e.target.value)} />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }}><Chip label="Opcional" size="small" /></Divider>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Si tu institución ya está registrada como entidad en Ecolab (cooperativa, aliado),
                  vincúlala aquí. Si no lo sabes, puedes dejarlo en blanco.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="ID de entidad en Ecolab"
                      value={form.ecolabEntidadId} onChange={(e) => set('ecolabEntidadId', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Tipo de entidad en Ecolab"
                      value={form.ecolabTipoEntidad} onChange={(e) => set('ecolabTipoEntidad', e.target.value)} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Paso 2 — Ubicación */}
            {step === 1 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Ubicación</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define el municipio donde opera la institución.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth required>
                      <InputLabel>Departamento</InputLabel>
                      <Select
                        value={form.departamentoId || ''}
                        label="Departamento"
                        onChange={(e) => { set('departamentoId', e.target.value); set('municipioId', ''); }}
                      >
                        {deptos.map((d) => (
                          <MenuItem key={d.departamentoID} value={d.departamentoID}>{d.nombre}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth required disabled={!form.departamentoId}>
                      <InputLabel>Municipio</InputLabel>
                      <Select
                        value={form.municipioId || ''}
                        label="Municipio"
                        onChange={(e) => {
                          set('municipioId', e.target.value);
                          const m = munis.find((mm) => String(mm.municipioID) === String(e.target.value));
                          if (m?.latitud && m?.longitud) {
                            setLatLng({ lat: m.latitud, lng: m.longitud });
                          }
                        }}
                      >
                        {munis.map((m) => (
                          <MenuItem key={m.municipioID} value={m.municipioID}>{m.nombre}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField size="small" fullWidth label="Dirección"
                      value={form.direccion} onChange={(e) => set('direccion', e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={buscandoUbicacion ? <CircularProgress size={14} /> : <MyLocationIcon />}
                      onClick={usarMiUbicacion}
                      disabled={buscandoUbicacion}
                      sx={{ mb: 1, color: '#2e7d32', borderColor: '#2e7d32' }}
                    >
                      {buscandoUbicacion ? 'Buscando tu ubicación…' : 'Usar mi ubicación'}
                    </Button>
                    {errorUbicacion && (
                      <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setErrorUbicacion(null)}>
                        {errorUbicacion}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <UbicacionMapaPicker
                      latitud={form.latitud}
                      longitud={form.longitud}
                      onCambio={setLatLng}
                      direccionParaGeocode={form.direccion}
                      ciudadParaGeocode={munis.find((m) => String(m.municipioID) === String(form.municipioId))?.nombre}
                      departamentoParaGeocode={deptos.find((d) => String(d.departamentoID) === String(form.departamentoId))?.nombre}
                      alto={300}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Paso 3 — Admin de la alianza */}
            {step === 2 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonAddAltIcon sx={{ color: '#2e7d32' }} /> Admin de la alianza
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Esta persona quedará como administradora (SuperAdmin) de la institución dentro de
                  {nombreProducto}. Le crearemos su cuenta en Ecolab (rol Aliado) y recibirá un correo para
                  definir su contraseña e ingresar al portal.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth required label="Nombres"
                      value={form.adminNombre} onChange={(e) => set('adminNombre', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Apellidos"
                      value={form.adminApellido} onChange={(e) => set('adminApellido', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth required label="Email"
                      value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Celular"
                      value={form.adminCelular} onChange={(e) => set('adminCelular', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Tipo de documento</InputLabel>
                      <Select value={form.adminTipoDocumento} label="Tipo de documento"
                        onChange={(e) => set('adminTipoDocumento', e.target.value)}>
                        {TIPOS_DOCUMENTO.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Número de documento"
                      value={form.adminDocumento} onChange={(e) => set('adminDocumento', e.target.value)} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Paso 4 — Confirmación */}
            {step === 3 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Confirma los datos</Typography>
                <Box sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#f9fbe7' }}>
                  {[
                    ['Institución', form.nombre],
                    ['Tipo', TIPOS_INSTITUCION.find((t) => t.value === form.tipo)?.label],
                    ['NIT', form.nit || '—'],
                    ['Email', form.email],
                    ['Teléfono', form.telefono || '—'],
                    ['Municipio', munis.find((m) => String(m.municipioID) === String(form.municipioId))?.nombre || '—'],
                    ['Departamento', deptos.find((d) => String(d.departamentoID) === String(form.departamentoId))?.nombre || '—'],
                    ['Dirección', form.direccion || '—'],
                    ['Coordenadas', form.latitud && form.longitud ? `${Number(form.latitud).toFixed(5)}, ${Number(form.longitud).toFixed(5)}` : 'No definidas'],
                    ['Admin de la alianza', `${form.adminNombre} ${form.adminApellido}`.trim()],
                    ['Email del admin', form.adminEmail],
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #e8e8e8' }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                  Al enviar, se crea la cuenta del admin en Ecolab (recibirá un correo para definir su
                  contraseña) y la institución queda registrada de inmediato en {nombreProducto}.
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {creada ? (
          <Button variant="contained" fullWidth onClick={onClose} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1a4d2e' } }}>
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit" disabled={enviando}>Cancelar</Button>
            <Box sx={{ flex: 1 }} />
            {step > 0 && <Button onClick={atras} disabled={enviando}>Atrás</Button>}
            {step < STEPS.length - 1 && (
              <Button variant="contained" onClick={siguiente} disabled={!puedeAvanzar}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1a4d2e' } }}>
                Siguiente
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button variant="contained" onClick={enviar} disabled={enviando}
                startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1a4d2e' } }}>
                Registrar institución
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}



